import React, { ReactElement, ReactNode } from "react";
import { Select } from "antd";
import { Rule } from "antd/es/form";

export interface LazySelectFieldProps {
  id: string;
  options: {
    value: string;
    label: string;
    group?: string;
  }[];
  showSearch?: boolean;
  placeholder?: string;
  readOnly?: boolean;
  mode?: "multiple";
  onSelect?: (value: string) => void;
  onChange?: (value: string[] | string) => void;
  onLoadMore: () => void;
  onSearch?: (input: string) => void;
  loading: boolean;
  isPaginationOver: boolean;
  fieldPrefix?: string;
  className?: string;
  allowClear?: boolean;
  onClear?: () => void;
  notFoundContent?: string | ReactNode;
  disabled?: boolean;
  fieldClassName?: string;
  labelColSpan?: number;
  labelAlign?: "left" | "right";
  initialValue?: string | string[];
  rules?: Rule[];
}

interface Option {
  value: string;
  label: string;
}

interface SelectGroup {
  label: string;
  item: Option[];
}

const LazySelectFormField = ({
  id,
  options,
  showSearch,
  placeholder,
  readOnly,
  mode,
  onSelect,
  onChange,
  onLoadMore,
  onSearch,
  loading,
  isPaginationOver,
  allowClear,
  onClear,
  notFoundContent,
  disabled,
  fieldClassName = "",
  initialValue
}: LazySelectFieldProps): ReactElement => {
  const groups: SelectGroup[] = [];
  options.forEach(item => {
    let isInclude = false;
    groups.forEach(groupItem => {
      if (groupItem.label === item.group) {
        groupItem.item.push(item);
        isInclude = true;
      }
    });
    if (!isInclude) {
      groups.push({ label: item.group || item.label, item: [item] });
    }
  });

  const handleSelect = (value: string) => {
    onSelect && onSelect(value);
  };

  const onScroll = (event: React.UIEvent<HTMLElement>) => {
    const target = event.currentTarget;
    if (!isPaginationOver) {
      if (
        !loading &&
        target &&
        Number(target.scrollTop) + Number(target.offsetHeight) + 20 >=
          Number(target.scrollHeight)
      ) {
        if (onLoadMore && !loading) onLoadMore();
      }
    }
  };

  return (
    <Select
      id={id}
      getPopupContainer={(triggerNode: HTMLElement) =>
        triggerNode.parentElement ? triggerNode.parentElement : triggerNode
      }
      showSearch={showSearch}
      mode={mode}
      disabled={readOnly || disabled}
      placeholder={placeholder}
      onSelect={handleSelect}
      onChange={onChange}
      onSearch={onSearch}
      filterOption={(input, option) =>
        ((option?.children as unknown) as string)
          ?.toUpperCase()
          .includes(input.toUpperCase())
      }
      allowClear={allowClear}
      onPopupScroll={onScroll}
      onClear={onClear}
      notFoundContent={notFoundContent}
      className={fieldClassName}
      defaultValue={initialValue}
      maxTagCount="responsive"
      listHeight={100}
    >
      {options.map((option, index) => (
        <Select.Option key={index} value={option.value}>
          {option.label}
        </Select.Option>
      ))}
      {loading && (
        <Select.Option key="loading" value={0}>
          Loading...
        </Select.Option>
      )}
    </Select>
  );
};

export { LazySelectFormField };
